// Google home — the Google specialist team's daily board.
//
// Same roster as Advertising Home, on ONE channel, with the panels a Google
// specialist works from. It IMPORTS the shared component set rather than
// copying it, for the same reason the Meta board does.
//
// ⚠️ NO ChannelComparison — it compares meta against google and is meaningless
// on a single-channel page. That slot is where the Google panels go.

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { PageHeader } from '../../components/layout/PageHeader';
import { SelectField } from '../../components/ui/SelectField';
import { accountFacetsFromBook } from '../../lib/advertisingHome/bookFilter';
import { ALL_ALERT_TYPES } from '../../lib/advertisingHome/alertsView';
import type { AlertType } from '../../lib/advertisingHome/alerts';
import { alertNoteKey } from '../../lib/advertisingHome/acknowledgements';
import { normalizeDisplayCurrency } from '../../lib/reporting/currencyConversion';
import { formatCurrencyAmounts } from '../../lib/reportingMoney';
import {
  PULSE_METRIC_OPTIONS,
  isPulseMetric,
} from '../../lib/advertisingHome/pulseSeries';
import type { ComparisonMode } from '../../types/advertisingHome';
import { DateRangePicker } from '../reporting/DateRangePicker';
import { getFxRates } from '../reporting/api';
import { PanelTitle } from '../reporting/PanelTitle';
import { COMPARISON_MODE_OPTIONS } from './homePrefs';
import type { AccountLinkKey } from '../../lib/accountLinks';
import { AlertsPanel } from './AlertsPanel';
import { AlertNotes } from './AlertNotes';
import { VerdictStrip, type HealthFilter } from './VerdictStrip';
import { PerformancePulse } from './PerformancePulse';
import { BookTable } from './BookTable';
import { WinsWorries } from './WinsWorries';
import { HomeFilterBar } from './HomeFilterBar';
import { ShareLossPanel } from './ShareLossPanel';
import { SearchTermWastePanel } from './SearchTermWastePanel';
import { SpecialistCountsNote } from './SpecialistCountsNote';
import { useSpecialistBoard } from './useSpecialistBoard';
import { getSearchTermWaste, getShareLoss } from './api';

// This board shows ONE account link beside every account name.
const GOOGLE_LINK_KEYS: AccountLinkKey[] = ['googleAdsUrl'];

export function GoogleHomePage(): React.JSX.Element {
  const board = useSpecialistBoard('google');
  const [healthFilter, setHealthFilter] = useState<HealthFilter>(null);
  const [alertsCollapsed, setAlertsCollapsed] = useState(false);
  const [alertTypes, setAlertTypes] = useState<Set<AlertType>>(() => new Set(ALL_ALERT_TYPES));

  const fxQuery = useQuery({
    queryKey: ['reporting-fx-rates'],
    queryFn: getFxRates,
    staleTime: 30 * 60 * 1000,
  });
  const fxSnapshot = fxQuery.data?.rates ?? null;
  const display = normalizeDisplayCurrency('native', fxSnapshot);

  const rangeValid = Boolean(board.range.from && board.range.to);

  // §5a — the best panel available on either page, and the data was already
  // shaped for it by the derived `campaign_competitiveness` view.
  const shareQuery = useQuery({
    queryKey: ['advertising-home-share-loss', board.scope, board.range.from, board.range.to],
    queryFn: () => getShareLoss(board.scope, board.range),
    enabled: rangeValid,
    staleTime: 5 * 60 * 1000,
  });

  /**
   * §11c — the search-term waste list. Its own query so a slow scan of
   * `search_terms_daily` never holds up the roster table above it.
   */
  const wasteQuery = useQuery({
    queryKey: ['advertising-home-search-term-waste', board.scope, board.range.from, board.range.to],
    queryFn: () => getSearchTermWaste(board.scope, board.range),
    enabled: rangeValid,
    staleTime: 5 * 60 * 1000,
  });

  const visibleAlerts = useMemo(
    () =>
      healthFilter === null
        ? board.visible
        : board.visible.filter(
            (a) => board.evaluation.healthByAccount.get(a.accountId) === healthFilter,
          ),
    [board.visible, board.evaluation, healthFilter],
  );
  const acknowledgedAlerts = useMemo(
    () =>
      healthFilter === null
        ? board.acknowledged
        : board.acknowledged.filter(
            (e) => board.evaluation.healthByAccount.get(e.alert.accountId) === healthFilter,
          ),
    [board.acknowledged, board.evaluation, healthFilter],
  );

  const bookRows = useMemo(
    () =>
      healthFilter === null
        ? board.bookRows
        : board.bookRows.filter((r) => r.health === healthFilter),
    [board.bookRows, healthFilter],
  );

  const overview = board.overviewQuery.data;
  const unattributed = board.bookQuery.data?.unattributedGoogle;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Google home"
        description="Every Google account on the roster — what is broken, where the auction is being lost, and who to hand it back to."
      />

      <div className="flex flex-wrap items-center gap-2">
        <DateRangePicker
          preset={board.preset}
          onPresetChange={board.setPreset}
          custom={board.custom}
          onCustomChange={board.setCustom}
        />
        <SelectField
          size="sm"
          aria-label="Comparison mode"
          value={board.comparisonMode}
          onValueChange={(v) => board.setComparisonMode(v as ComparisonMode)}
          options={COMPARISON_MODE_OPTIONS}
          className="min-w-[13rem]"
        />
        <span className="ml-auto text-xs text-muted-foreground">
          Google data to {board.asOf.google ?? '—'}
        </span>
      </div>

      {/* ⚠️⚠️ §5g — CONVERSIONS ARE NEVER CORRECTED. The BigQuery transfer runs
          with NO refresh window, so nothing is ever re-pulled: today's stored
          conversion counts are permanently the first-day figure, and delayed
          and view-through conversions never land. An AM glancing weekly may not
          notice; a SPECIALIST judging yesterday every single morning will
          systematically misread it. Stated on the page rather than in a
          footnote, for the same reason the no-spend alert refuses to read a
          partial day. */}
      <p className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
        <span>
          Google conversions are <strong>never re-pulled</strong> — the transfer has no refresh
          window, so the last few days are the first-day figure and will under-report. Delayed
          and view-through conversions never land at all. Judge recent days as provisional.
        </span>
      </p>

      {overview && board.channelBook.length > 0 && (
        <HomeFilterBar
          facets={accountFacetsFromBook(board.channelBook)}
          selection={board.filterSel}
          onChange={board.setFilterSel}
          amOptions={board.amOptions}
        />
      )}

      {board.overviewQuery.isLoading && (
        <p className="text-sm text-muted-foreground">Loading the Google roster…</p>
      )}
      {board.overviewQuery.isError && (
        <p className="text-sm text-destructive">
          Could not load the roster — {(board.overviewQuery.error as Error).message}
        </p>
      )}

      {overview && board.accounts.length === 0 && !board.overviewQuery.isLoading && (
        <div className="rounded-lg border border-border bg-card p-6 text-sm">
          <p className="font-medium">No accounts are running Google right now.</p>
          <p className="mt-1 text-muted-foreground">
            This board lists accounts with Google enabled. Accounts running Meta only appear on
            the Meta board instead.
          </p>
        </div>
      )}

      {board.accounts.length > 0 && (
        <>
          <VerdictStrip counts={board.counts} filter={healthFilter} onFilter={setHealthFilter} />
          <SpecialistCountsNote channelLabel="Google" />

          <AlertsPanel
            showLinkIcons
            linkKeys={GOOGLE_LINK_KEYS}
            alerts={visibleAlerts}
            acknowledged={acknowledgedAlerts}
            onAcknowledge={board.onAcknowledge}
            onUnacknowledge={board.onUnacknowledge}
            busy={board.busy}
            collapsed={alertsCollapsed}
            onToggleCollapsed={() => setAlertsCollapsed(!alertsCollapsed)}
            typeFilter={alertTypes}
            onTypeFilterChange={setAlertTypes}
            platformFilter="all"
            onPlatformFilterChange={() => {}}
            // §11a — who to hand it back to. Specialist boards only.
            managerFor={(a) => board.amNameOf(a.accountId)}
            // §11b — the badge and the thread read the SAME list: the panel
            // calls this ONCE per row and hands the result to both, so a count
            // that disagrees with the notes underneath it is not expressible.
            notesFor={(a) => board.notesByAlert.get(alertNoteKey(a)) ?? []}
            renderAlertExtra={(a, notes) => (
              <AlertNotes
                notes={notes}
                onAdd={(body) => board.onAddNote(a, body)}
                busy={board.noteBusy}
              />
            )}
          />

          {/* §5f — the attribution gap. The Google specialist owns the match
              tokens, so this is their worklist rather than an AM's footnote.
              ⚠️ Counted per CUSTOMER upstream, not per campaign: one unlinked
              account with 40 campaigns is one thing to fix, not forty. */}
          {unattributed && unattributed.spend.length > 0 && (
            <p className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden />
              <span>
                {formatCurrencyAmounts(unattributed.spend)} across {unattributed.campaigns}{' '}
                campaign{unattributed.campaigns === 1 ? '' : 's'} has no match token, so it sits
                on no account — and it is excluded from every figure on this page.
              </span>
            </p>
          )}

          {/* §5a — the panel this page exists for. */}
          <ShareLossPanel
            rows={shareQuery.data?.rows ?? []}
            loading={shareQuery.isLoading}
            nameOf={board.nameOf}
            amNameOf={board.amNameOf}
            linkKeys={GOOGLE_LINK_KEYS}
          />

          {/* §11c — next to the share-loss split, because both answer "where
              is the money going that should not be". ⚠️ Narrowed to the
              board's own accounts, like every other section. */}
          <SearchTermWastePanel
            rows={(wasteQuery.data?.rows ?? []).filter((r) =>
              board.accounts.some((a) => a.id === r.accountId),
            )}
            capped={wasteQuery.data?.capped ?? false}
            nameOf={board.nameOf}
            loading={wasteQuery.isLoading}
            linkKeys={GOOGLE_LINK_KEYS}
          />

          <section className="space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <PanelTitle>Performance pulse</PanelTitle>
              {/* ⚠️ Metric only — the pulse CHANNEL is fixed by which board you
                  are on. Persisted under this board's OWN grid key, so a Google
                  specialist's choice never reaches an AM's Advertising Home. */}
              <span className="ml-auto">
                <SelectField
                  size="sm"
                  aria-label="Pulse metric"
                  value={board.pulseMetric}
                  onValueChange={(v) => isPulseMetric(v) && board.setPulseMetric(v)}
                  options={PULSE_METRIC_OPTIONS}
                  className="min-w-[6.5rem]"
                />
              </span>
            </div>
            <PerformancePulse
              cards={board.pulse}
              display={display}
              snapshot={fxSnapshot}
              metric={board.pulseMetric}
              channel="google"
              nativeCurrency={board.bookCurrency}
              channels={[board.channel]}
            />
          </section>

          <section className="space-y-2">
            <PanelTitle className="block">
              The roster — {board.range.from} → {board.range.to}
            </PanelTitle>
            {board.bookQuery.isLoading ? (
              <p className="text-sm text-muted-foreground">Loading the range…</p>
            ) : (
              <BookTable
                rows={bookRows}
                alertsByAccount={board.alertsByAccount}
                sortKey={board.sortKey}
                sortDir={board.sortDir}
                onSort={board.onSort}
                showPacing={false}
                display={display}
                snapshot={fxSnapshot}
                channels={[board.channel]}
                linkKeys={GOOGLE_LINK_KEYS}
              />
            )}
          </section>

          {board.bookQuery.data && (
            <WinsWorries
              wins={board.winsWorries.wins}
              worries={board.winsWorries.worries}
              leadsBack={board.winsWorries.leadsBack}
              leadsGone={board.winsWorries.leadsGone}
              nameOf={board.nameOf}
              linkKeys={GOOGLE_LINK_KEYS}
            />
          )}
        </>
      )}
    </div>
  );
}
